/**
 * Main-thread RTCPeerConnection diagnostics, installed before the engine
 * starts (the network layer creates its peer connections on the main
 * thread through the WebRTC bridge, so a wrapped constructor catches them).
 *
 * - `?forceRelay=1` (or `localStorage.forceRelay = '1'`): every peer
 *   connection runs with `iceTransportPolicy: 'relay'`, i.e. media takes the
 *   TURN path instead of the direct one. A live A/B switch for sessions
 *   whose direct path misbehaves (the Safari↔Safari IPv6 stalls of
 *   2026-10-19): if the stalls vanish on the relayed path, the path is the
 *   cause, not the browser. Latched at page load, so it survives the SPA
 *   navigation from `/` to `/stream/:id`.
 * - `[rtc-pair]` log line whenever a connection's selected ICE candidate
 *   pair appears or changes: candidate types, protocol, address family and
 *   addresses of both ends, plus the pair RTT. Without it the `[conn-type]`
 *   line only says host/srflx/prflx/relay, which cannot tell an IPv6 direct
 *   path from an IPv4 one.
 *
 * Loopback decoder connections (`iceServers: []`) are left alone.
 */
const LOG_PREFIX = '[rtc-pair]';
const PAIR_POLL_MS = 5_000;
export function parseRtcDiagnosticsFlags(input) {
    const params = new URLSearchParams(input.search);
    const param = params.get('forceRelay');
    const on = (value) => value !== null && ['1', 'true', 'on'].includes(value);
    return { forceRelay: on(param) || on(input.localStorageValue) };
}
function describeCandidate(stat) {
    if (stat === undefined)
        return '?';
    const address = typeof stat.address === 'string'
        ? stat.address
        : typeof stat.ip === 'string'
            ? stat.ip
            : undefined;
    const family = address === undefined ? '?' : address.includes(':') ? 'v6' : 'v4';
    return `${stat.candidateType ?? '?'}/${stat.protocol ?? '?'} ${family} ${address ?? '?'}:${stat.port ?? '?'}`;
}
async function readSelectedPair(pc) {
    let report;
    try {
        report = await pc.getStats();
    }
    catch {
        return undefined;
    }
    const byId = new Map();
    let selectedPairId;
    report.forEach((stat) => {
        byId.set(stat.id, stat);
        if (stat.type === 'transport' &&
            typeof stat.selectedCandidatePairId === 'string') {
            selectedPairId = stat.selectedCandidatePairId;
        }
    });
    let pair = selectedPairId !== undefined ? byId.get(selectedPairId) : undefined;
    if (pair === undefined) {
        for (const stat of byId.values()) {
            if (stat.type === 'candidate-pair' &&
                stat.nominated === true &&
                stat.state === 'succeeded') {
                pair = stat;
                break;
            }
        }
    }
    if (pair === undefined)
        return undefined;
    return {
        local: describeCandidate(byId.get(pair.localCandidateId)),
        remote: describeCandidate(byId.get(pair.remoteCandidateId)),
        rttMs: typeof pair.currentRoundTripTime === 'number'
            ? Math.round(pair.currentRoundTripTime * 1000)
            : undefined,
    };
}
function watchPair(pc, id) {
    let last = '';
    const poll = async () => {
        if (pc.connectionState === 'closed')
            return;
        const pair = await readSelectedPair(pc);
        if (pair !== undefined) {
            const key = `${pair.local}|${pair.remote}`;
            if (key !== last) {
                last = key;
                console.log(LOG_PREFIX, JSON.stringify({
                    pc: id,
                    ...pair,
                    state: pc.connectionState,
                }));
            }
        }
        setTimeout(poll, PAIR_POLL_MS);
    };
    pc.addEventListener('connectionstatechange', () => {
        if (pc.connectionState === 'connected') {
            // biome-ignore lint/nursery/noFloatingPromises: diagnostics only
            poll();
        }
    });
}
let installed = false;
/** Call once, before the engine creates its first peer connection. */
export function installRtcDiagnostics() {
    if (installed || typeof RTCPeerConnection === 'undefined')
        return;
    installed = true;
    let localStorageValue = null;
    try {
        localStorageValue = window.localStorage.getItem('forceRelay');
    }
    catch {
        // storage unavailable
    }
    const flags = parseRtcDiagnosticsFlags({
        search: window.location.search,
        localStorageValue,
    });
    const Orig = RTCPeerConnection;
    let seq = 0;
    const Wrapped = function (config) {
        const loopback = config !== undefined &&
            Array.isArray(config.iceServers) &&
            config.iceServers.length === 0;
        const effective = !loopback && flags.forceRelay
            ? { ...(config ?? {}), iceTransportPolicy: 'relay' }
            : config;
        const pc = new Orig(effective);
        if (!loopback) {
            watchPair(pc, seq++);
        }
        return pc;
    };
    Wrapped.prototype = Orig.prototype;
    Object.setPrototypeOf(Wrapped, Orig);
    window.RTCPeerConnection =
        Wrapped;
    if (flags.forceRelay) {
        console.log(`${LOG_PREFIX} forceRelay: every peer connection uses iceTransportPolicy 'relay'`);
    }
}
